import {
    IconBrandInstagram,
    IconBrandWhatsapp,
    IconBrandYoutube,
    IconLocationFilled,
    IconMail,
    IconPhone,
    IconLock,
} from '@tabler/icons-react';
import React from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
    return (
        <footer className="w-full bg-gray-900 text-white relative overflow-hidden">
            {/* Decorative top gradient line */}
            <div className="h-1 w-full bg-gradient-to-r from-[#2a1273] via-pumpkinToast-500 to-[#2a1273]" />

            {/* Decorative blobs */}
            <div className="absolute top-0 left-0 w-72 h-72 bg-[#2a1273]/20 rounded-full blur-[140px]" />
            <div className="absolute bottom-0 right-0 w-64 h-64 bg-pumpkinToast-500/10 rounded-full blur-[120px]" />

            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
                {/* Top area - social links */}
                <div className="flex flex-col sm:flex-row items-center justify-between py-8 border-b border-white/10">
                    <p className="text-gray-400 text-sm hidden sm:block">
                        Get connected with us on social networks
                    </p>
                    <div className="flex items-center gap-4">
                        <Link
                            to="#"
                            className="w-10 h-10 bg-white/[0.07] hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 border border-white/10"
                        >
                            <IconBrandInstagram size={20} />
                        </Link>
                        <Link
                            to="#"
                            className="w-10 h-10 bg-white/[0.07] hover:bg-red-600 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 border border-white/10"
                        >
                            <IconBrandYoutube size={20} />
                        </Link>
                        <Link
                            to="https://wa.me/917907252303"
                            className="w-10 h-10 bg-white/[0.07] hover:bg-green-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300 border border-white/10"
                        >
                            <IconBrandWhatsapp size={20} />
                        </Link>
                    </div>
                </div>

                {/* Main footer content */}
                <div className="py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* About */}
                    <div className="lg:col-span-1 text-left">
                        <div className="flex items-center gap-2 mb-4">
                            <img src="/assets/darulLogo.png" alt="logo" className="h-10 w-10" loading="lazy" />
                            <h3 className="font-AndoraModernSerif text-xl font-bold text-pumpkinToast-400">
                                DarulHadees
                            </h3>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed">
                            Dedicated to the preservation and dissemination of authentic Islamic knowledge,
                            rooted in the traditions of the Prophet Muhammad (PBUH).
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div className="text-left">
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-5">
                            Quick Links
                        </h4>
                        <ul className="space-y-3">
                            {[
                                { name: 'Home', path: '/home' },
                                { name: 'About', path: '/about' },
                                { name: 'Courses', path: '/courses' },
                                { name: 'Gallery', path: '/gallery' },
                                { name: 'Contact', path: '/contact' },
                            ].map(link => (
                                <li key={link.path}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-pumpkinToast-400 transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-gray-600 rounded-full group-hover:bg-pumpkinToast-400 transition-colors" />
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Programs */}
                    <div className="text-left">
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-5">
                            Programs
                        </h4>
                        <ul className="space-y-3">
                            {['Hadith Studies', 'Islamic Research', 'Online Courses', 'Seminars', 'Library'].map(item => (
                                <li key={item}>
                                    <span className="text-gray-400 text-sm flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-gray-600 rounded-full" />
                                        {item}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="text-left">
                        <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-5">
                            Contact
                        </h4>
                        <div className="space-y-4">
                            <p className="flex items-start gap-3 text-gray-400 text-sm leading-relaxed">
                                <IconLocationFilled size={20} className="text-pumpkinToast-400 mt-0.5 flex-shrink-0" />
                                Poocholamad, Kannattippadi (PO), Vengara (Via) Malappuram Dt. Kerala - 676304, India
                            </p>
                            <p className="flex items-center gap-3 text-gray-400 text-sm">
                                <IconMail size={18} className="text-pumpkinToast-400 flex-shrink-0" />
                                <a href="mailto:info@darulhadees.com" className="hover:text-pumpkinToast-400 transition-colors">
                                    info@darulhadees.com
                                </a>
                            </p>
                            <p className="flex items-center gap-3 text-gray-400 text-sm">
                                <IconPhone size={18} className="text-pumpkinToast-400 flex-shrink-0" />
                                <a href="tel:+918606391538" className="hover:text-pumpkinToast-400 transition-colors">
                                    +91 8606391538
                                </a>
                            </p>
                            <p className="flex items-center gap-3 text-gray-400 text-sm">
                                <IconPhone size={18} className="text-pumpkinToast-400 flex-shrink-0" />
                                <a href="tel:+917736847386" className="hover:text-pumpkinToast-400 transition-colors">
                                    +91 7736847386
                                </a>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="border-t border-white/10 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-gray-500 text-sm">
                        © 2025 <span className="text-pumpkinToast-400 font-medium">DarulHadees</span>. All rights reserved.
                    </p>
                    <div className="flex items-center gap-3">
                        <span className="text-gray-700 text-xs hidden sm:inline">•</span>
                        <p className="text-gray-600 text-xs">
                            Islamic Research Center • Kerala, India
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
